<script setup lang="ts">
import ColorBadge from "@/components/ui/ColorBadge.vue";
</script>

<template>
  <div class="api-key-usage-tips-modal-body">
    <div class="text-base-regular">
      Before launching an interactive authentication, <code>skore.login()</code> will check if an
      API key is set in the environment variables. If it is, it will use it to authenticate. If it
      is not, an interactive authentication will be triggered.
    </div>

    <ColorBadge color="blue" left-icon="icon-info-circle-2">
      <p>
        API keys grant full access to your account, so treat them like
        <span class="text-base-medium">passwords</span>.
      </p>

      <p>
        Never hardcode keys in scripts or notebooks, and never share them in screenshots, gists, or
        chat logs. Store keys in environment variables or local configuration files (like
        <code>.env</code> file) that are excluded from version control (for example via
        <code>.gitignore</code>). Avoid printing or logging API keys, even in debug mode, and rotate
        a key immediately if you suspect it has been exposed. When collaborating, use dedicated keys
        per user or per environment so you can revoke compromised keys without disrupting
        everything.
      </p>
    </ColorBadge>

    <div class="text-base-regular">
      To use an API key in your code, we recommend to use an environment variable. There are various
      ways to do this:
    </div>

    <table class="tabular-list">
      <thead>
        <tr>
          <th>Strategy</th>
          <th>How</th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <td>bash</td>
          <td>
            <code>export SKORE_HUB_API_KEY=your-api-key</code>
          </td>
        </tr>

        <tr>
          <td>powershell</td>
          <td>
            <code>$env:SKORE_HUB_API_KEY = "your-api-key"</code>
          </td>
        </tr>

        <tr>
          <td>in a .env file</td>
          <td>
            <p>install <code>python-dotenv</code> then</p>
            <p>
              <code>import dotenv; dotenv.load_dotenv()</code>
            </p>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.api-key-usage-tips-modal-body {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-16);

  p {
    margin-bottom: var(--numbers-8);
  }

  table {
    table-layout: auto;
  }

  code {
    border: var(--stroke-width-md) solid var(--border-tertiary);
    border-radius: var(--numbers-4);
    background-color: var(--background-tertiary);
  }
}
</style>
